import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, ShieldCheck, Lock, ArrowDownToLine } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* NdaDownloadGate
 * ---------------
 * A small lead-capture modal that gates the mutual-NDA download. Submitting
 * the form:
 *   1. Logs a row to MongoDB via POST /api/legal/nda-request
 *   2. Notifies Ashlee (best-effort) via Resend
 *   3. Returns the actual file URL, which we kick off as a download
 *
 * Used in two places (Partners page + /security page).
 * Usage:
 *   <NdaDownloadGate trigger={<button>...</button>} testidPrefix="partners" />
 *
 * If you'd rather skip the gate, simply use a plain <a download href=...>.
 */
export default function NdaDownloadGate({ trigger, testidPrefix = "nda" }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", org_name: "", role: "", message: "" });

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.name.trim() || !form.email.trim() || !form.org_name.trim()) {
            toast("Please share your name, work email, and organization.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/legal/nda-request", form);
            const url = r.data?.download_url || "/Memory-Lane-Mutual-NDA.txt";
            // Kick off the download via a synthetic anchor — keeps the modal open
            // so we can show the friendly confirmation copy.
            const a = document.createElement("a");
            a.href = url;
            a.download = "Memory-Lane-Mutual-NDA.txt";
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("NDA downloaded — we'll be in touch.");
            setOpen(false);
            setForm({ name: "", email: "", org_name: "", role: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't process — try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <span
                onClick={() => setOpen(true)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
                role="button"
                tabIndex={0}
                data-testid={`${testidPrefix}-nda-open`}
                className="inline-block"
            >
                {trigger}
            </span>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid={`${testidPrefix}-nda-modal`}>
                    <DialogHeader>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center">
                                <ShieldCheck size={18} strokeWidth={1.7} />
                            </span>
                            <DialogTitle className="text-xl font-heading font-medium">Download the mutual NDA</DialogTitle>
                        </div>
                        <DialogDescription className="text-sm text-[#4C5551] leading-relaxed">
                            Tell us who you are so we can follow up. We&apos;ll email you the same NDA you&apos;re downloading — and Ashlee will reach out within a business day.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3 mt-2" data-testid={`${testidPrefix}-nda-form`}>
                        <Field label="Your name *" value={form.name} onChange={(v) => update("name", v)} testid="nda-name" placeholder="Pat Sunset" autoFocus />
                        <Field label="Work email *" type="email" value={form.email} onChange={(v) => update("email", v)} testid="nda-email" placeholder="director@sunset-care.example" />
                        <Field label="Organization *" value={form.org_name} onChange={(v) => update("org_name", v)} testid="nda-org" placeholder="Sunset Care Provider" />
                        <Field label="Role (optional)" value={form.role} onChange={(v) => update("role", v)} testid="nda-role" placeholder="Director of Caregiver Services" />
                        <label className="block">
                            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Anything to share? (optional)</span>
                            <textarea
                                value={form.message}
                                onChange={(e) => update("message", e.target.value)}
                                data-testid={`${testidPrefix}-nda-field-message`}
                                rows={2}
                                placeholder="Timeline you're working toward, # families, etc."
                                className="w-full px-3 py-2 rounded-2xl bg-white border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                            />
                        </label>
                        <p className="text-xs text-[#6E7A74] inline-flex items-center gap-1">
                            <Lock size={11} /> We only use this to reach back to you. See <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-dark underline">Privacy</a>.
                        </p>
                        <DialogFooter className="mt-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                data-testid={`${testidPrefix}-nda-cancel`}
                                className="px-4 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#4C5551] hover:bg-cream-dark/30"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid={`${testidPrefix}-nda-submit`}
                                className="px-5 py-2 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowDownToLine size={14} />}
                                Download NDA
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, autoFocus }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                data-testid={`nda-field-${testid}`}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
            />
        </label>
    );
}
